<script lang="ts">
  import Select from '../ui/Select.svelte';
  import Button from '../ui/Button.svelte';
  import Disclosure from '../ui/Disclosure.svelte';
  import InlineMessage from '../ui/InlineMessage.svelte';
  import { BUILD_INFO } from '../platform/build-info';
  import {
    FEEDBACK_LIMITS,
    feedbackRequestSchema,
    type FeedbackReply,
    type FeedbackRequest,
  } from '../../shared/feedback';
  import { feedbackDevice, sendFeedback } from '../api/feedback';

  let { getProfileCredential }: { getProfileCredential: () => Promise<string> } = $props();
  let open = $state(false);
  let kind = $state<'bug' | 'suggestion'>('bug');
  let summary = $state('');
  let details = $state('');
  let expected = $state('');
  let includeDevice = $state(true);
  let sending = $state(false);
  let packet = $state<FeedbackRequest | null>(null);
  let result = $state<FeedbackReply | { status: 'unknown' | 'profile_error' | 'client_error' } | null>(null);
  const editableAgain = $derived(
    result &&
      ['retry', 'rate_limited', 'unavailable', 'invalid', 'unauthorized', 'failed'].includes(result.status),
  );
  const checkAgain = $derived(result && ['pending', 'unknown'].includes(result.status));

  async function submit() {
    if (sending || result?.status === 'created' || result?.status === 'uncertain') return;
    sending = true;
    try {
      let credential: string;
      try {
        credential = await getProfileCredential();
      } catch {
        result = { status: 'profile_error' };
        return;
      }
      if (!packet) {
        const parsed = feedbackRequestSchema.safeParse({
          submissionId: crypto.randomUUID(),
          kind,
          summary,
          details,
          expected,
          client: {
            ...BUILD_INFO,
            submittedAt: new Date().toISOString(),
            device: includeDevice
              ? feedbackDevice(
                  navigator.userAgent,
                  navigator.maxTouchPoints,
                  window.innerWidth,
                  window.innerHeight,
                )
              : null,
          },
        });
        if (!parsed.success) {
          result = { status: 'invalid' };
          return;
        }
        packet = parsed.data;
      }
      result = await sendFeedback(packet, credential);
    } catch {
      result = { status: packet ? 'unknown' : 'client_error' };
    } finally {
      sending = false;
    }
  }

  function editReport() {
    packet = null;
    result = null;
  }
  function reset() {
    editReport();
    summary = '';
    details = '';
    expected = '';
  }
</script>

<div class="feedback">
  <Disclosure
    bind:open
    id="feedback-content"
    full
    label="Report a Bug"
    expandedLabel="Close Bug Report"
    icon="bug"
  >
    <form
      id="feedback-form"
      onsubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <h2>Report a bug or suggestion</h2>
      <p class="hint">
        Your text will be sent to the maintainers’ private GitHub issue tracker. Please don’t include
        passwords, credentials, or sensitive information.
      </p>
      {#if result?.status !== 'created'}
        <label for="feedback-kind">Report type</label>
        <Select
          id="feedback-kind"
          bind:value={kind}
          disabled={sending || !!packet}
          options={[
            { value: 'bug', label: 'Bug' },
            { value: 'suggestion', label: 'Suggestion' },
          ]}
        />
        <label for="feedback-summary">Summary</label>
        <input
          id="feedback-summary"
          bind:value={summary}
          minlength="5"
          maxlength={FEEDBACK_LIMITS.summary}
          required
          readonly={sending || !!packet}
        />
        <label for="feedback-details">What happened, or what would you like?</label>
        <textarea
          id="feedback-details"
          bind:value={details}
          minlength="10"
          maxlength={FEEDBACK_LIMITS.details}
          required
          readonly={sending || !!packet}></textarea>
        <label for="feedback-expected">Expected behavior (optional)</label>
        <textarea
          id="feedback-expected"
          class="expected"
          bind:value={expected}
          maxlength={FEEDBACK_LIMITS.expected}
          readonly={sending || !!packet}></textarea>
        <label class="device-choice">
          <input type="checkbox" bind:checked={includeDevice} disabled={sending || !!packet} />
          Include browser family/major version, OS family, and window size
        </label>
        <p class="hint">
          App {BUILD_INFO.version} ({BUILD_INFO.revision}), build time, submission times, and environment are
          included automatically. No name, room details, gameplay words, or logs are attached.
        </p>
      {/if}
      <InlineMessage
        intent={sending || !result || result.status === 'pending'
          ? 'info'
          : result.status === 'created'
            ? 'success'
            : 'error'}
      >
        {#if sending}
          Sending… Please keep this screen open until confirmation.
        {:else if result?.status === 'created'}
          Thanks! Report received: #{result.issueNumber}.
        {:else if result?.status === 'pending'}
          Your report is still awaiting confirmation. Check again shortly.
        {:else if result?.status === 'unknown'}
          Could not confirm delivery. Your text is kept here. Check this same submission rather than creating
          another report.
        {:else if result?.status === 'uncertain'}
          GitHub delivery could not be confirmed. Do not submit the same report again. Keep the reference
          below for the maintainers.
        {:else if result?.status === 'retry'}
          GitHub did not accept the report. Your text is kept here. Try again after {result.retryAfterSeconds} seconds.
        {:else if result?.status === 'rate_limited'}
          Report limit reached. Your text is kept here. Try again later.
        {:else if result?.status === 'unavailable'}
          Bug reporting is not configured for this environment yet. Your text is kept here.
        {:else if result?.status === 'client_error'}
          Could not prepare the report. Use a current browser over HTTPS. Your text is kept here.
        {:else if result?.status === 'profile_error'}
          Could not connect to profile storage. Your text is kept here. Try again.
        {:else if result?.status === 'unauthorized'}
          Could not authenticate this browser. Copy your report before reloading or contacting the
          maintainers.
        {:else if result?.status === 'invalid'}
          Use a summary of at least 5 characters and a description of at least 10 characters.
        {:else if result?.status === 'failed' || result?.status === 'conflict'}
          This submission could not be delivered. Keep the reference below and contact the maintainers.
        {/if}
      </InlineMessage>
      {#if packet}<p class="hint reference">Submission reference: {packet.submissionId}</p>{/if}
      {#if result && ['created', 'uncertain', 'failed', 'conflict'].includes(result.status)}
        <Button variant="ghost" size="small" onclick={reset}>Report a Different Issue</Button>
      {:else if result?.status !== 'uncertain' && result?.status !== 'failed' && result?.status !== 'conflict'}
        <Button type="submit" variant="primary" aria-busy={sending} disabled={sending}
          >{sending
            ? 'Sending…'
            : checkAgain
              ? 'Check Submission'
              : packet
                ? 'Retry Report'
                : 'Submit Report'}</Button
        >
      {/if}
      {#if editableAgain && packet}
        <Button variant="ghost" size="small" aria-busy={sending} disabled={sending} onclick={editReport}
          >Edit Report</Button
        >
      {/if}
    </form>
  </Disclosure>
</div>

<style>
  .feedback {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  textarea {
    min-width: 0;
    min-height: 140px;
    resize: vertical;
  }
  textarea.expected {
    min-height: 90px;
  }
  .device-choice {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 44px;
  }
  .device-choice input {
    width: auto;
    accent-color: var(--theme-accent);
  }
  .reference {
    overflow-wrap: anywhere;
  }
</style>
